// Model for a "Tuning Log": a JSON-serializable history of step response captures (image +
// flight log configuration + notes) for a craft, so changes can be tracked over time and replayed
// as context for AI tuning advice.
// Ported from https://github.com/bph838/rotorflight-blackbox-bellsandwhistles (js/tuning_log.js).
// That version ran under NW.js and persisted itself directly to a file on disk (Node `fs`/
// `crypto`); this app is a plain browser SPA, so persistence instead lives in
// stores/tuningLog.js (localStorage, with explicit export/import) and hashing uses a small
// dependency-free function instead of Node's `crypto` module - the id only needs to be stable and
// collision-resistant for local de-duplication, not cryptographically secure.

function pad2(n) {
  return (n < 10 ? "0" : "") + n;
}

/**
 * A small, fast, deterministic string hash (cyrb53), returned as a fixed-length hex string.
 * Stands in for the original's `md5(text)` - only used as a stable local id, never for anything
 * security-sensitive.
 */
export function hashHex(text) {
  let h1 = 0xdeadbeef ^ 0;
  let h2 = 0x41c6ce57 ^ 0;

  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  const hi = (h2 >>> 0).toString(16).padStart(8, "0");
  const lo = (h1 >>> 0).toString(16).padStart(8, "0");
  return hi + lo;
}

/**
 * `logIndex`/`logCount` identify which sub-log within a multi-log BBL file this id is for. A
 * timestamp alone isn't always unique across sub-logs of the same file - e.g. a flight controller
 * without an RTC reports the same placeholder "Log start datetime" for every sub-log, so
 * resolveLogDateTimes() below estimates the same date/time for a run of them. When there's
 * more than one log in the file, fold the log's index into the id so each sub-log still gets a
 * distinct, stable id even when their timestamps collide; a single-log file keeps the plain
 * timestamp-only id (unaffected by whatever index that one log happens to have).
 */
export function makeId(timestampIso, logIndex, logCount) {
  const key = logCount > 1 ? `${timestampIso}|${logIndex}` : String(timestampIso);
  return hashHex(key);
}

/**
 * description: free text about the heli itself (size, make/model, motor, blades...), sent to the
 * AI as context with every analysis.
 */
export function create(name, craftName, description) {
  const createdDate = new Date().toISOString();

  return {
    formatVersion: 1,
    logId: hashHex(`${name || ""}|${createdDate}`),
    name: name || craftName || "Tuning Log",
    craftName: craftName || "",
    description: description || "",
    createdDate,
    entries: [],
    // Tombstones ({ id, deletedAt }) for deleted entries, so a sync can't bring them back from
    // another copy of the log - see tuning_log_sync.js.
    deletedEntries: [],
  };
}

/**
 * Flattens a flight log's system configuration (PID gains, filters, rates, etc.) into a readable
 * text block, both for display and as context given to the AI.
 */
export function buildConfigSummary(sysConfig) {
  const lines = [];
  const keys = Object.keys(sysConfig || {}).sort();

  for (const key of keys) {
    const value = sysConfig[key];

    if (value === null || value === undefined || typeof value === "function") {
      continue;
    }

    try {
      lines.push(`${key}: ${JSON.stringify(value)}`);
    } catch {
      // Skip values that can't be serialized
    }
  }

  return lines.join("\n");
}

// Header values that differ from one flight to the next without any setting having changed -
// left out when deciding whether two entries were flown on the same config.
const PER_FLIGHT_CONFIG_KEYS = new Set(["Log start datetime", "vbatref"]);

/**
 * True when two config summaries (see buildConfigSummary) hold the same settings, ignoring
 * per-flight header values like the log start time. An empty/missing summary never matches.
 */
export function configsMatch(a, b) {
  if (!a || !b) return false;

  const settings = (summary) =>
    summary
      .split("\n")
      .filter((line) => !PER_FLIGHT_CONFIG_KEYS.has(line.slice(0, line.indexOf(": "))))
      .join("\n");

  return settings(a) === settings(b);
}

/**
 * The settings in config summary `current` that differ from `previous`, one line each - the
 * current line with the old value after it ("rollPID: [50,60,20] (was [45,60,20])"), or for a
 * setting only in `previous`, that it's gone. Per-flight header values are left out.
 */
export function configChanges(previous, current) {
  const parse = (summary) => {
    const map = new Map();
    for (const line of String(summary || "").split("\n")) {
      const at = line.indexOf(": ");
      const key = at === -1 ? line : line.slice(0, at);
      if (line && !PER_FLIGHT_CONFIG_KEYS.has(key)) map.set(key, at === -1 ? "" : line.slice(at + 2));
    }
    return map;
  };

  const before = parse(previous);
  const after = parse(current);
  const changes = [];

  for (const [key, value] of after) {
    if (!before.has(key)) {
      changes.push(`${key}: ${value} (new)`);
    } else if (before.get(key) !== value) {
      changes.push(`${key}: ${value} (was ${before.get(key)})`);
    }
  }
  for (const [key, value] of before) {
    if (!after.has(key)) changes.push(`${key}: (no longer in the log, was ${value})`);
  }
  return changes;
}

/**
 * Entry id -> the chronologically previous entry, for each entry whose config is unchanged from
 * that previous entry (i.e. nothing was re-tuned between the two flights).
 */
export function findSameConfigAsPrevious(entries) {
  const result = new Map();
  const chronological = [...(entries || [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  for (let i = 1; i < chronological.length; i++) {
    if (configsMatch(chronological[i].config, chronological[i - 1].config)) {
      result.set(chronological[i].id, chronological[i - 1]);
    }
  }
  return result;
}

/**
 * Read and validate a flight log's own "Log start datetime" header in isolation, with no
 * fallback - use this when a made-up substitute (see resolveLogDateTimes() below) would be
 * misleading, e.g. showing several distinct recordings as if they happened at the same instant.
 *
 * Flight controllers without an RTC report the header as "0000-01-01T00:00:00.000+00:00" instead
 * of omitting it, which parses as a valid (but useless) Date - treated as absent here too.
 */
export function parseLogStartDateTime(sysConfig) {
  const raw = sysConfig && sysConfig["Log start datetime"];

  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime()) && parsed.getUTCFullYear() >= 2000) {
      return parsed.toISOString();
    }
  }

  return null;
}

/**
 * Estimates a start date/time for every sub-log in a BBL file, including ones whose own header
 * lacks a valid "Log start datetime" (see parseLogStartDateTime) - e.g. a flight controller with
 * no RTC reports the same useless placeholder for every sub-log it writes, so on their own they
 * can't be told apart or placed in time at all. Each sub-log's raw microsecond "time" field isn't
 * usable for this - it's re-parsed fresh per sub-log (see flightlog_index.js) and isn't a
 * continuous clock across them, so it can't tell us the real gap between one sub-log ending and
 * the next starting.
 *
 * Instead, chains by each sub-log's own duration, assuming zero gap between consecutive sub-logs
 * (the best available guess with no other information) - trusting a known start time outright, and
 * for a run of unknown ones between two known points (or at either end of the file), walking
 * outward from the *nearest* known/anchored sub-log in file order: forward from an earlier one by
 * adding durations, or backward from a later one by subtracting them. This is what lets a leading
 * run of unknown sub-logs (e.g. an RTC that only syncs partway through the file) borrow a *later*
 * sub-log's known date/time, not just an earlier one.
 *
 * When there's no known start time anywhere in the file, the whole file instead chains forward from
 * `fallbackIso` - typically the flight log file's own `lastModified` time (epoch ms, from the
 * browser `File` object - see appStore.logFileLastModified), since that's stable across copying the
 * file (unlike a "created" time, which would reset to "now" when the file is copied off an SD
 * card).
 *
 * `logs`: per sub-log `{ startDateTime: isoString|null, durationMs: number }`, in file order.
 * Returns a same-length/order array of `{ dateTime: isoString|null, isCalculated: boolean }`.
 */
export function resolveLogDateTimes(logs, fallbackIso) {
  const firstKnownIndex = logs.findIndex((log) => log.startDateTime);
  const results = [];

  // Forward pass: chain from each known start time (or, if the file has none at all, from
  // fallbackIso) through the unknown sub-logs that follow it. Sub-logs before the first known one
  // are left unresolved here (null) when the file does have a known start time somewhere - the
  // backward pass below fills those in from that known point instead of from fallbackIso.
  let cursor = firstKnownIndex === -1 ? fallbackIso || null : null;
  for (let i = 0; i < logs.length; i++) {
    const log = logs[i];
    if (log.startDateTime) {
      results.push({ dateTime: log.startDateTime, isCalculated: false });
      cursor = addMs(log.startDateTime, log.durationMs);
    } else if (i < firstKnownIndex) {
      // Placeholder - always overwritten by the backward pass below, since firstKnownIndex > i
      // here means firstKnownIndex > 0, so that pass runs and covers every index below it.
      results.push(null);
    } else {
      results.push({ dateTime: cursor, isCalculated: true });
      cursor = cursor ? addMs(cursor, log.durationMs) : null;
    }
  }

  // Backward pass: fill in any leading run of unknown sub-logs by walking backward from the first
  // known start time, subtracting each preceding sub-log's own duration in turn.
  if (firstKnownIndex > 0) {
    let cursorBack = logs[firstKnownIndex].startDateTime;
    for (let i = firstKnownIndex - 1; i >= 0; i--) {
      cursorBack = addMs(cursorBack, -logs[i].durationMs);
      results[i] = { dateTime: cursorBack, isCalculated: true };
    }
  }

  return results;
}

function addMs(iso, ms) {
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) {
    return null;
  }

  return new Date(time + (ms || 0)).toISOString();
}

/**
 * options: { image, config, notes, craftName, timestamp, calculatedDateTime, logIndex, logCount }
 */
export function addEntry(log, options) {
  const timestamp = options.timestamp || new Date().toISOString();

  const entry = {
    id: makeId(timestamp, options.logIndex, options.logCount),
    timestamp,
    craftName: options.craftName || "",
    image: options.image,
    config: options.config || "",
    notes: options.notes || "",
    // When this entry was added to the log. Its id comes from the flight log, so deleting an entry
    // and capturing the same flight again gives the same id - this is what lets the new entry win
    // over the old one's deletion tombstone (see tuning_log_sync.js).
    addedAt: new Date().toISOString(),
  };

  // Persisted on the entry itself (rather than re-derived from the timestamp later) so the
  // "No RTC" badge still shows correctly for a past entry after its originating flight
  // log file is no longer open - see resolveLogDateTimes() above.
  if (options.calculatedDateTime) {
    entry.calculatedDateTime = true;
  }

  if (options.ai) {
    entry.ai = options.ai;
  }

  log.entries.push(entry);

  return entry;
}

/**
 * Adds one AI response's token usage (tuning_ai.js summariseUsage) onto an entry's running total
 * (entry.ai.usage). Either may be missing.
 */
export function addUsage(total, usage) {
  if (!usage) return total || null;
  const result = { ...(total || {}) };
  for (const [key, value] of Object.entries(usage)) {
    result[key] = (result[key] || 0) + value;
  }
  return result;
}

export function buildFilename(log) {
  const now = new Date();
  const stamp = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}_${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`;

  const safeName = (log.name || "TuningLog").replace(/[^a-z0-9_-]+/gi, "_");

  return `RF_TUNING_LOG_${safeName}_${stamp}.json`;
}

/**
 * Returns an error message if `log` doesn't look like a tuning log file, or null if it's fine to
 * use. Guards against a user picking an unrelated JSON file (or a corrupted one) via Import -
 * without this, e.g. a `null` or non-object `entries` element would later throw when code
 * elsewhere dereferences it directly.
 */
export function validationError(log) {
  if (!log || typeof log !== "object" || Array.isArray(log)) {
    return "This file is not a tuning log.";
  }

  if (typeof log.formatVersion !== "number") {
    return "This file is not a tuning log.";
  }

  if (log.entries !== undefined) {
    if (!Array.isArray(log.entries)) {
      return 'This file is not a tuning log (its "entries" field is malformed).';
    }

    for (const entry of log.entries) {
      if (!entry || typeof entry !== "object") {
        return 'This file is not a tuning log (its "entries" field is malformed).';
      }
    }
  }

  if (log.deletedEntries !== undefined && !Array.isArray(log.deletedEntries)) {
    return 'This file is not a tuning log (its "deletedEntries" field is malformed).';
  }

  return null;
}
